import React from "react";
import { useTranslation } from "react-i18next";
import { TrendingUp, TrendingDown, Activity } from "lucide-react";

/**
 * QENVA · Évolution Guardian™ strip.
 *
 * Horizontal progression trail of the Guardian Score™ over the last N months.
 * Supports both the legacy 4-point trail (from overview-v3) and the new
 * 12-point series (from /dashboard/evolution-12m). Real snapshots are bold;
 * interpolated points (no historical data yet) are zinc-300.
 *
 * Includes a "X mois réels" badge so executives know how much of the trail
 * is grounded in real history vs. the elegant fallback synthesis.
 */
export default function EvolutionGuardianStrip({ evolution, target = 100, testId = "evolution-guardian" }) {
  const { t } = useTranslation("dashboard");
  if (!evolution || !Array.isArray(evolution.trail) || evolution.trail.length === 0) {
    return null;
  }
  const trail = evolution.trail;
  const delta = evolution.delta_this_month || 0;
  const realMonths = typeof evolution.real_months === "number" ? evolution.real_months : null;
  const totalMonths = trail.length;
  const seriesComplete = !!evolution.series_complete;
  const isCompact = totalMonths > 6;

  const TrendIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Activity;
  const trendColor = delta > 0 ? "text-emerald-600" : delta < 0 ? "text-rose-500" : "text-zinc-400";
  const trendBg = delta > 0 ? "bg-emerald-50" : delta < 0 ? "bg-rose-50" : "bg-zinc-100";

  return (
    <section
      className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6"
      data-testid={testId}
    >
      <div className="flex items-center flex-wrap gap-x-8 gap-y-4">
        <div className="flex items-center gap-3 shrink-0">
          <div className={`w-10 h-10 rounded-xl grid place-items-center ${trendBg} ${trendColor}`}>
            <TrendIcon size={18} strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-emerald-600">
                {t("evolution_strip.kicker")}
              </p>
              {realMonths !== null && (
                <span
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wider ${
                    seriesComplete
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-zinc-100 text-zinc-500"
                  }`}
                  data-testid={`${testId}-real-badge`}
                  title={
                    seriesComplete
                      ? t("evolution_strip.real_months_complete_hint")
                      : t("evolution_strip.real_months_partial_hint")
                  }
                >
                  {t("evolution_strip.real_months_badge", { real: realMonths, total: totalMonths })}
                </span>
              )}
            </div>
            <p
              className="font-display font-bold text-qenva-navy text-[15px] tracking-tight"
              data-testid={`${testId}-delta`}
            >
              {t("evolution_strip.pts_this_month", { delta: delta > 0 ? `+${delta}` : delta })}
            </p>
          </div>
        </div>

        {/* Trail */}
        <ol
          className={`flex items-center flex-1 min-w-[280px] ${
            isCompact ? "gap-1.5 sm:gap-2.5" : "gap-3 sm:gap-5"
          }`}
          data-testid={`${testId}-trail`}
        >
          {trail.map((p, i) => {
            const isLast = i === trail.length - 1;
            const isInterpolated = p.interpolated;
            const scoreSize = isCompact ? "text-[15px] sm:text-[17px]" : "text-[20px] sm:text-[24px]";
            const labelSize = isCompact ? "text-[8.5px]" : "text-[10px]";
            return (
              <li
                key={i}
                className={`flex items-center ${isCompact ? "gap-1.5" : "gap-3"}`}
                data-testid={`${testId}-trail-${i}`}
              >
                <div className="flex flex-col items-center">
                  <span
                    className={`font-display font-bold leading-none tabular-nums ${scoreSize} ${
                      isLast
                        ? "text-emerald-600"
                        : isInterpolated
                          ? "text-zinc-300"
                          : "text-qenva-navy/80"
                    }`}
                    title={isInterpolated ? t("evolution_strip.interpolated_hint") : t("evolution_strip.real_hint")}
                  >
                    {p.score ?? "—"}
                  </span>
                  <span
                    className={`uppercase tracking-wider font-bold mt-1 ${labelSize} ${
                      isInterpolated ? "text-zinc-300" : "text-zinc-400"
                    }`}
                  >
                    {p.month}
                  </span>
                </div>
                {i < trail.length - 1 && !isCompact && (
                  <span className="text-zinc-300 text-[14px] hidden sm:inline">→</span>
                )}
              </li>
            );
          })}
        </ol>

        {/* Target */}
        <div className="text-right shrink-0 ml-auto" data-testid={`${testId}-target`}>
          <p className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-zinc-400">{t("evolution_strip.target")}</p>
          <p className="font-display font-bold text-qenva-navy text-[24px] tabular-nums leading-none">
            {target}
            <span className="text-zinc-400 text-[14px] font-normal"> /100</span>
          </p>
        </div>
      </div>
    </section>
  );
}
